import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  TablePagination,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  School as GradeIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchGrades,
  createGrade,
  updateGrade,
  deleteGrade,
  clearError,
  clearSuccess,
  setSelectedGrade,
  clearSelectedGrade,
} from '../../../features/grades/gradeSlice';
import GradeFormDialog from './GradeFormDialog';
import ConfirmationDialog from '../../UIElements/ConfirmationDialog';
import Spinner from '../../UIElements/Spinner';
import ToastNotification from '../../UIElements/ToastNotification';
import useNotification from '../../../hooks/useNotification';

const GradeConfiguration = () => {
  const dispatch = useDispatch();
  const { grades, loading, error, success, selectedGrade } = useSelector(
    (state) =>
      state.grades || {
        grades: [],
        loading: false,
        error: null,
        success: false,
        selectedGrade: null,
      },
  );

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [gradeToDelete, setGradeToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);

  const { notification, showSuccess, showError, hideNotification } =
    useNotification();
  // Load grades on component mount
  useEffect(() => {
    dispatch(fetchGrades());
  }, [dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      showSuccess(
        `Grade ${dialogMode === 'add' ? 'added' : 'updated'} successfully!`,
      );
      dispatch(clearSuccess());
      handleCloseFormDialog();
    }
  }, [success, dialogMode, showSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      showError(getErrorMessage(error));
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  // Filter grades based on search term
  const filteredGrades = (grades || []).filter(
    (grade) =>
      grade?.grade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grade?.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Paginated grades
  const paginatedGrades = filteredGrades.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleOpenAddDialog = () => {
    setDialogMode('add');
    dispatch(clearSelectedGrade());
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (grade) => {
    setDialogMode('edit');
    dispatch(setSelectedGrade(grade));
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    dispatch(clearSelectedGrade());
  };

  const handleSubmitGrade = async (formData) => {
    setIsSubmitting(true);
    try {
      if (dialogMode === 'add') {
        await dispatch(createGrade(formData)).unwrap();
      } else {
        await dispatch(
          updateGrade({
            id: selectedGrade.id,
            data: formData,
          }),
        ).unwrap();
        dispatch(fetchGrades());
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (grade) => {
    setGradeToDelete(grade);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeletingId(gradeToDelete.id);
    try {
      await dispatch(deleteGrade(gradeToDelete.id)).unwrap();
      showSuccess('Grade deleted successfully!');
      setConfirmDialogOpen(false);
      setGradeToDelete(null);
      dispatch(fetchGrades());
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setIsDeletingId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (typeof error === 'string') {
      return error;
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.error) {
      return error.error;
    }
    if (typeof error === 'object') {
      return JSON.stringify(error);
    }
    return 'An unexpected error occurred';
  };

  return (
    <Box className='p-4'>
      {/* Header Section */}
      <Card className='mb-4'>
        <CardContent>
          <Box className='flex items-center justify-between mb-4 flex-wrap'>
            <Box className='flex items-center gap-2 flex-wrap'>
              <GradeIcon color='primary' />
              <Typography variant='h5' className='text-blue-700 font-bold'>
                Grade Configuration
              </Typography>
            </Box>
            <Button
              variant='contained'
              color='primary'
              startIcon={loading ? <Spinner size={16} /> : <AddIcon />}
              onClick={handleOpenAddDialog}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Add New Grade'}
            </Button>
          </Box>

          <Typography variant='body2' className='text-gray-600 mb-4'>
            Configure grading system with percentage ranges and grade values
          </Typography>

          {/* Search Bar */}
          <TextField
            size='small'
            placeholder='Search grades...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  {loading ? <Spinner size={20} /> : <SearchIcon />}
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </CardContent>
      </Card>

      {/* Loading Spinner */}
      {loading && !grades.length && (
        <Card>
          <CardContent>
            <Box className='flex flex-col justify-center items-center py-8 gap-3'>
              <Spinner size={40} />
              <Typography variant='body2' color='textSecondary'>
                Loading grades configuration...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Error Alert */}
      {error && !loading && (
        <Alert severity='error' className='mb-4'>
          {getErrorMessage(error)}
        </Alert>
      )}

      {/* Grades Table */}
      {!loading && (
        <Card>
          <CardContent sx={{ padding: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ maxHeight: '60vh', overflow: 'auto' }}
            >
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell>
                      <strong>S.No</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Grade</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Percentage Range</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedGrades.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align='center' sx={{ py: 4 }}>
                        <Typography variant='body1' color='textSecondary'>
                          {searchTerm
                            ? 'No grades found matching your search.'
                            : 'No grades configured yet.'}
                        </Typography>
                        {!searchTerm && (
                          <Button
                            variant='outlined'
                            color='primary'
                            startIcon={<AddIcon />}
                            onClick={handleOpenAddDialog}
                            sx={{ mt: 2 }}
                          >
                            Add First Grade
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedGrades.map((grade, index) => (
                      <TableRow key={grade.id} hover>
                        <TableCell>
                          <Typography>{index + 1}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{grade.grade}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {grade?.rangeFrom}% - {grade.rangeTo}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box className='flex gap-1'>
                            <Tooltip title='Edit Grade'>
                              <IconButton
                                size='small'
                                color='primary'
                                onClick={() => handleOpenEditDialog(grade)}
                                disabled={loading}
                              >
                                {loading ? (
                                  <Spinner size={16} />
                                ) : (
                                  <EditIcon fontSize='small' />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Delete Grade'>
                              <IconButton
                                size='small'
                                color='error'
                                onClick={() => handleOpenDeleteDialog(grade)}
                                disabled={loading}
                              >
                                {loading ? (
                                  <Spinner size={16} />
                                ) : (
                                  <DeleteIcon fontSize='small' />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {filteredGrades.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={filteredGrades.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ '.MuiTablePagination-toolbar': { px: 2 } }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Grade Dialog */}
      <GradeFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSubmit={handleSubmitGrade}
        mode={dialogMode}
        initialData={selectedGrade}
        loading={loading || isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        title='Delete Grade'
        message={`Are you sure you want to delete grade "${gradeToDelete?.grade}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setConfirmDialogOpen(false);
          setGradeToDelete(null);
        }}
        confirmText='Delete'
        cancelText='Cancel'
        severity='error'
        loading={loading || isDeletingId === gradeToDelete?.id}
      />

      {/* Toast Notifications */}
      <ToastNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </Box>
  );
};

export default GradeConfiguration;
